import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import {
  fetchCompanies,
  createCompany,
  updateCompany,
  deleteCompany,
} from '../../api/companies';

function formatMailQuota(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return new Intl.NumberFormat('fr-FR').format(Math.max(0, Math.floor(n)));
}

export default function CompaniesManage() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState('');
  const [editQuotaMail, setEditQuotaMail] = useState('25');
  const [editHubupCatchall, setEditHubupCatchall] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchCompanies();
      setCompanies(res?.data?.companies || []);
    } catch (e) {
      setError('Impossible de charger les entreprises.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await createCompany(trimmed);
      setName('');
      await load();
    } catch {
      setError('Création impossible.');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (c) => {
    setEditing(c._id);
    setEditName(c.name || '');
    setEditQuotaMail(String(c.quotaMail ?? 25));
    setEditHubupCatchall(c.hubupCatchallEnabled === true);
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditName('');
    setEditQuotaMail('25');
    setEditHubupCatchall(false);
  };

  const saveEdit = async () => {
    if (!editing) return;
    const quota = Number(String(editQuotaMail).replace(/\s/g, ''));
    if (!Number.isFinite(quota) || quota < 0) {
      setError('Le quota e-mail doit être un nombre ≥ 0.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateCompany(editing, {
        name: editName.trim(),
        quotaMail: Math.floor(quota),
        hubupCatchallEnabled: editHubupCatchall,
      });
      setEditing(null);
      await load();
    } catch {
      setError('Mise à jour impossible.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Supprimer « ${c.name} » ?`)) return;
    try {
      await deleteCompany(c._id);
      await load();
    } catch {
      setError('Suppression impossible.');
    }
  };

  return (
    <>
      <Helmet>
        <title>Entreprises — CRM</title>
      </Helmet>
      <Header />
      <main className="main-content">
        <Breadcrumb items={[{ label: 'Entreprises' }]} />
        <div className="max-w-2xl mx-auto p-4 space-y-6">
          <div>
            <h1 className="text-xl font-semibold text-foreground">
              Gestion des entreprises
            </h1>
            <p className="text-xs text-muted-foreground mt-1">
              Quota e-mail CRM par entreprise (1 envoi = 1 crédit). Défaut à la
              création : 25. Le catch-all HubUP (IMAP) s&apos;active par
              entreprise ; l&apos;e-mail expéditeur se configure par communauté
              dans HubUP.
            </p>
          </div>

          <form onSubmit={handleCreate} className="flex gap-2">
            <input
              type="text"
              className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="Nom de la nouvelle entreprise"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Button type="submit" disabled={saving || !name.trim()}>
              Créer
            </Button>
          </form>

          {error ? <p className="text-xs text-error">{error}</p> : null}

          {loading ? (
            <p className="text-sm text-muted-foreground">Chargement…</p>
          ) : (
            <ul className="divide-y divide-border border border-border rounded-lg">
              {companies.map((c) => (
                <li
                  key={c._id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3"
                >
                  {editing === c._id ? (
                    <div className="flex flex-col sm:flex-row gap-2 flex-1 w-full">
                      <input
                        className="flex-1 rounded-md border border-input px-2 py-1 text-sm"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="Nom"
                      />
                      <label className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          Quota e-mail
                        </span>
                        <input
                          type="number"
                          min={0}
                          step={1}
                          className="w-28 rounded-md border border-input px-2 py-1 text-sm"
                          value={editQuotaMail}
                          onChange={(e) => setEditQuotaMail(e.target.value)}
                        />
                      </label>
                      <label className="flex items-center gap-2 shrink-0 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editHubupCatchall}
                          onChange={(e) => setEditHubupCatchall(e.target.checked)}
                        />
                        <span className="text-xs text-muted-foreground">
                          Catch-all HubUP
                        </span>
                      </label>
                      <div className="flex gap-2 shrink-0">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={cancelEdit}
                        >
                          Annuler
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => void saveEdit()}
                          disabled={saving}
                        >
                          OK
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div>
                        <p className="text-sm font-medium">{c.name}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Quota e-mail : {formatMailQuota(c.quotaMail ?? 25)}
                          {c.hubupCatchallEnabled ? (
                            <span className="ml-2 text-primary">
                              · Catch-all HubUP activé
                            </span>
                          ) : null}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => startEdit(c)}
                        >
                          Modifier
                        </Button>
                        {c.slug !== 'deepblock' ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => void handleDelete(c)}
                          >
                            Supprimer
                          </Button>
                        ) : null}
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </>
  );
}
